import {
  CoverageAreaDriverDetails,
  CoverageAreaDriverItem,
  CoverageAreaProps,
} from "store/modules/Logistics/types.store";
import { COVERAGE_AREA_TYPE, CoverageAreaType } from "types/coverageAreaType";

type ApiCoverageAreaDriver = {
  priority?: number;
  driver?: {
    id?: string;
    name?: string;
    surname?: string;
    status?: string;
    enabled?: boolean;
  };
  id?: string;
  details?: CoverageAreaDriverDetails;
};

type ApiCoverageAreaDetail = Omit<
  CoverageAreaProps,
  "type" | "drivers" | "map"
> & {
  type: string;
  map?: CoverageAreaProps["map"] | null;
  drivers?: ApiCoverageAreaDriver[];
};

type WrappedCoverageAreaDetail = {
  data: ApiCoverageAreaDetail;
};

const COVERAGE_AREA_TYPES: Record<string, CoverageAreaType> = {
  dealermap: COVERAGE_AREA_TYPE.DEALER_MAP,
  dealerstore: COVERAGE_AREA_TYPE.DEALER_STORE,
  driverstore: COVERAGE_AREA_TYPE.DRIVER_STORE,
};

const EMPTY_MAP = { link: "", geoJson: {} };

export function normalizeCoverageAreaType(type: string): CoverageAreaType {
  const normalizedType = COVERAGE_AREA_TYPES[type.toLowerCase()];
  if (!normalizedType) {
    throw new Error(`Unsupported coverage area type: ${type}`);
  }
  return normalizedType;
}

function mapApiDriverItem(
  item: ApiCoverageAreaDriver,
  dealerId: string,
): CoverageAreaDriverItem | null {
  if (item.details?.id) {
    return {
      id: item.details.id,
      priority: Number(item.priority ?? 0),
      details: {
        ...item.details,
        dealerId: item.details.dealerId || dealerId,
      },
    };
  }

  const driverId = (item.driver?.id ?? item.id ?? "").trim();
  if (!driverId) {
    return null;
  }

  const driverStatus = (item.driver?.status ?? "").trim().toLowerCase();
  const enabled =
    item.driver?.enabled ??
    (driverStatus ? driverStatus?.toLowerCase() === "online" : true);

  return {
    id: driverId,
    priority: Number(item.priority ?? 0),
    details: {
      id: driverId,
      dealerId,
      name: item.driver?.name ?? "",
      ...(item.driver?.surname ? { surname: item.driver.surname } : {}),
      enabled,
    },
  };
}

export function mapCoverageAreaDrivers(
  drivers: ApiCoverageAreaDriver[] | undefined,
  dealerId: string,
): CoverageAreaDriverItem[] {
  return (drivers ?? [])
    .map((item) => mapApiDriverItem(item, dealerId))
    .filter((item): item is CoverageAreaDriverItem => item !== null);
}

function unwrapCoverageAreaDetail(
  responseBody: ApiCoverageAreaDetail | WrappedCoverageAreaDetail,
): ApiCoverageAreaDetail {
  if (
    responseBody &&
    typeof responseBody === "object" &&
    "data" in responseBody &&
    responseBody.data &&
    typeof responseBody.data === "object" &&
    "id" in responseBody.data
  ) {
    return responseBody.data;
  }

  return responseBody as ApiCoverageAreaDetail;
}

export function mapCoverageAreaDetail(
  responseBody: ApiCoverageAreaDetail | WrappedCoverageAreaDetail,
  fallbackDealerId = "",
): CoverageAreaProps {
  const coverageArea = unwrapCoverageAreaDetail(responseBody);
  const dealerId = coverageArea.dealer?.id || fallbackDealerId;

  return {
    ...coverageArea,
    map: coverageArea.map ?? EMPTY_MAP,
    type: normalizeCoverageAreaType(String(coverageArea.type)),
    drivers: mapCoverageAreaDrivers(coverageArea.drivers, dealerId),
    openingHours: coverageArea.openingHours ?? [],
    holidayOpeningHours: coverageArea.holidayOpeningHours ?? [],
    holidays: coverageArea.holidays ?? [],
    hasDriver: Boolean(
      coverageArea.hasDriver || (coverageArea.drivers ?? []).length > 0,
    ),
    storeUri: coverageArea.storeUri ?? "",
  };
}
